import { useCallback, useEffect } from "react";
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { Editor } from "@tiptap/core";
import { ArrowLeft, FileDown, Save, TriangleAlert } from "lucide-react";

import type { DocumentBlock, DocumentPage } from "@/api/document/block";
import type { FloatDraft } from "@/api/document/floats";
import { editorBlocks } from "@/components/document-editor/editor";
import { Button } from "@/components/ui/button";
import { UnsavedChangesGuard } from "@/components/shared/dialogs/unsaved-changes-guard";
import { useTermEdit } from "@/hooks/use-terms";
import { TermosLabels } from "@/lib/labels/termos";

interface TermChromeProps {
	termId: number;
	name: string;
	onNameChange(name: string): void;
	dirty: boolean;
	editor: Editor;
	buildDocument(blocks: DocumentBlock[]): {
		pages: DocumentPage[];
		floats: FloatDraft[];
	};
	onSaved(name: string, blocks: DocumentBlock[]): void;
	toolbar: ReactNode;
}

export function TermChrome({
	termId,
	name,
	onNameChange,
	dirty,
	editor,
	buildDocument,
	onSaved,
	toolbar,
}: TermChromeProps) {
	const edit = useTermEdit();

	const save = useCallback(() => {
		const trimmed = name.trim();

		if (!dirty || edit.isPending || trimmed === "") {
			return;
		}

		const blocks = editorBlocks(editor);
		const document = buildDocument(blocks);

		edit.mutate(
			{ id: termId, name: trimmed, ...document },
			{ onSuccess: () => onSaved(trimmed, blocks) },
		);
	}, [dirty, edit, editor, termId, name, buildDocument, onSaved]);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
				event.preventDefault();
				save();
			}
		};

		document.addEventListener("keydown", onKey);

		return () => document.removeEventListener("keydown", onKey);
	}, [save]);

	return (
		<header className="workspace-sticky">
			<div className="flex flex-col gap-3 px-4 py-2.5 md:flex-row md:items-center md:justify-between">
				<div className="flex min-w-0 flex-1 items-center gap-3">
					<Link
						to="/clinica"
						className="inline-flex shrink-0 items-center gap-1 rounded-sm text-sm text-primary transition-colors hover:text-primary-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
					>
						<ArrowLeft className="size-4" />
						{TermosLabels.editor.back}
					</Link>

					<input
						value={name}
						aria-label={TermosLabels.editor.nameAria}
						onChange={(event) => onNameChange(event.target.value)}
						className="min-w-0 flex-1 truncate rounded-sm bg-transparent font-display text-base text-foreground outline-none transition-colors hover:bg-primary/[0.04] focus-visible:bg-primary/[0.06]"
					/>
				</div>

				<div className="flex shrink-0 items-center gap-2">
					<Button
						type="button"
						data-slot="term-export"
						variant="secondary"
						size="sm"
						disabled={dirty}
						onClick={() => window.open(`/terms/export/${termId}`, "_blank")}
					>
						<FileDown />
						{TermosLabels.editor.export}
					</Button>

					<Button
						size="sm"
						className="h-11 md:h-8"
						disabled={!dirty || edit.isPending}
						onClick={save}
					>
						<Save />
						{dirty && TermosLabels.editor.save}
						{!dirty && TermosLabels.editor.saved}
					</Button>
				</div>
			</div>

			{toolbar}

			{dirty && (
				<p className="flex items-center gap-1.5 px-4 pb-2 text-sm font-semibold text-warning">
					<TriangleAlert className="size-4 shrink-0" aria-hidden />
					{TermosLabels.editor.dirtyBanner}
				</p>
			)}

			<UnsavedChangesGuard
				blocking={dirty}
				title={TermosLabels.editor.leaveTitle}
				description={TermosLabels.editor.leaveConfirm}
				stayLabel={TermosLabels.editor.stay}
				leaveLabel={TermosLabels.editor.leave}
			/>
		</header>
	);
}
